import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '@portal/app/shared/services/auth.service';
import { DashboardService } from '@portal/app/shared/services/dashboard.service';
import { UserModel } from '@portal/app/shared/models/userModel';
import { ApiService } from '@portal/app/shared/services/api.service';
import { StorageService } from '@portal/app/shared/services/storage.service';
import { ClientStoreService } from '@portal/app/dashboard/geo-doe-config/data-stores/client-store.service';
import {
  Observable,
  Subject,
  take,
  takeUntil,
  throwError,
  retry,
  catchError,
  of
} from 'rxjs';
import { MessageService } from 'primeng/api';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import {
  getOktaIdTokenObj,
  getReturnUrl
} from '@portal/app/shared/helpers/helpers';
import { PendoService } from '@portal/app/shared/services/pendo.service';
import { WindowService } from '@portal/app/shared/services/window.service';

@Component({
  selector: 'portal-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  public isRedirecting = false;
  public submitting = false;
  private readonly returnUrlCookieKey = 'returnUrl';
  public isMobileModalVisible = false;
  public isDontShowAgainEnabled = false;
  private readonly dontShowAgainKey = 'isDontShowAgainEnabled';
  private destroy$ = new Subject<void>();
  private hasProcessedLogin = false;

  constructor(
    public authService: AuthService,
    public dashboardService: DashboardService,
    public apiService: ApiService,
    public store: StorageService,
    private messageService: MessageService,
    private readonly clientStoreService: ClientStoreService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    public readonly pendoService: PendoService,
    private readonly windowService: WindowService,
    @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth
  ) {}

  ngOnInit(): void {
    if (this.isMobile()) {
      this.isMobileModalVisible = true;
    }

    const dontShowAgainFlag = localStorage.getItem(this.dontShowAgainKey);
    if (dontShowAgainFlag === 'true') {
      this.isMobileModalVisible = false;
    }

    const returnUrl: string = this.route.snapshot.queryParams.returnUrl;

    if (returnUrl) {
      this.store.set(this.returnUrlCookieKey, returnUrl, true);
    }

    this.processToken(returnUrl);

    this.authService.idToken$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async () => {
        try {
          await this.processToken(returnUrl);
        } catch {
          this.router.navigateByUrl('/login');
        }
      });

    if (this.router.url.includes('/force-login')) {
      this.authService.signIn().catch((e) => throwError(() => new Error(e)));
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.hasProcessedLogin = false;
  }

  public isMobile() {
    const userAgent = navigator.userAgent || navigator.vendor;
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      userAgent
    );
  }

  public closeMobileModal() {
    this.isMobileModalVisible = false;

    if (this.isDontShowAgainEnabled) {
      localStorage.setItem(this.dontShowAgainKey, 'true');
    }
  }

  public onCheckboxChange() {
    this.isDontShowAgainEnabled = !this.isDontShowAgainEnabled;
  }

  /**
   * Single entry point for token validation and login processing.
   * Validates the current token from the token manager and initiates login if valid.
   * Called both on init (immediate check) and when idToken$ emits (reactive check).
   */
  private async processToken(returnUrl: string | null): Promise<void> {
    if (this.hasProcessedLogin || this.submitting) {
      return;
    }

    const idTokenObj = await getOktaIdTokenObj(this.oktaAuth);

    if (
      idTokenObj?.idToken &&
      !this.oktaAuth.tokenManager.hasExpired(idTokenObj)
    ) {
      this.hasProcessedLogin = true;
      if (returnUrl && !this.isValidHelpDocsUrl(returnUrl)) {
        this.isRedirecting = true;
      }
      this.processLogin(idTokenObj.idToken);
    }
  }

  /**
   * Checks if a URL points to support.measured.com with HTTPS
   */
  private isValidHelpDocsUrl(url: string): boolean {
    if (!url) {
      return false;
    }

    try {
      const urlObj = new URL(url);
      return (
        urlObj.protocol === 'https:' &&
        urlObj.hostname.toLowerCase() === 'support.measured.com'
      );
    } catch {
      return false;
    }
  }

  private processLogin(idToken: string): void {
    if (!idToken || this.submitting) {
      return;
    }

    this.submitting = true;

    this.apiService
      .getUser(true)
      .pipe(retry(2))
      .subscribe({
        next: (user) => {
          this.authService.setUser(user);
          this.initStateAndNavigate(new UserModel(user));
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 401) {
            this.submitting = true;
            this.authService
              .login(idToken)
              .pipe(take(1))
              .subscribe({
                next: (userWithModel) => {
                  if (userWithModel == null) {
                    this.submitting = false;
                    console.error(`Unable to authenticate user account.`);
                  } else {
                    this.initStateAndNavigate(userWithModel);
                  }
                },
                error: (e) => {
                  this.handleLoginError(e);
                }
              });
          } else {
            this.handleServerError(error);
          }
        }
      });
  }

  private handleLoginError(error: unknown, summary = 'Error'): void {
    this.messageService.add({
      severity: 'error',
      summary,
      sticky: true,
      detail: `${String(error)} - Please contact support to resolve the issue`
    });

    this.oktaAuth.tokenManager.clear();
    this.store.remove('user');
    this.store.remove('id_token');
    this.submitting = false;
  }

  private handleServerError(error: HttpErrorResponse): void {
    this.messageService.add({
      severity: 'warn',
      summary: 'Server Error',
      sticky: true,
      detail:
        'The server is experiencing issues. Please try again in a few minutes.'
    });

    this.submitting = false;

    console.error('Error:', error);
  }

  private initStateAndNavigate(userWithModel: UserModel): void {
    this.apiService
      .getClients()
      .pipe(take(1))
      .subscribe({
        next: (resp) => {
          this.clientStoreService.allClients = resp.clients;
          this.getPathAfterAuth(userWithModel)
            .pipe(take(1))
            .subscribe({
              next: (path) => {
                if (this.isValidHelpDocsUrl(path)) {
                  this.authService
                    .generateHelpDocsJwt()
                    .pipe(
                      take(1),
                      catchError(() => of(null))
                    )
                    .subscribe({
                      complete: () => {
                        this.windowService.navigateToHref(path);
                      }
                    });
                } else {
                  const queryParams = Object.fromEntries(
                    Object.entries(this.route.snapshot.queryParams).filter(
                      ([key]) => key !== this.returnUrlCookieKey
                    )
                  );

                  this.router
                    .navigate([path], {
                      replaceUrl: true,
                      queryParams
                    })
                    .then(() => {
                      // Fallback to landing page if returnUrl was invalid (404)
                      if (this.router.url.startsWith('/login')) {
                        this.isRedirecting = false;
                        this.submitting = false;
                        this.dashboardService
                          .landingPage(userWithModel)
                          .pipe(take(1))
                          .subscribe((landingPath) => {
                            this.router.navigate([`/a/${landingPath}`], {
                              replaceUrl: true
                            });
                          });
                      }
                    });
                }
              },
              error: () => (this.submitting = false)
            });
        },
        error: (err) => {
          this.handleLoginError(err, 'No Permission');
          throwError(() => new Error(err));
        }
      });
  }

  private getPathAfterAuth(user: UserModel): Observable<string> {
    return new Observable<string>((subscriber) => {
      const storedUrl = this.store.get(this.returnUrlCookieKey, true);
      const validReturnUrl = getReturnUrl(storedUrl);

      if (storedUrl) {
        this.store.remove(this.returnUrlCookieKey, true);
      }

      if (validReturnUrl) {
        subscriber.next(validReturnUrl);
        subscriber.complete();
      } else {
        this.dashboardService
          .landingPage(user)
          .pipe(take(1))
          .subscribe({
            next: (path) => {
              subscriber.next(`/a/${path}`);
              subscriber.complete();
            }
          });
      }
    });
  }
}
